// src/modules/units/tyres/shared/tyre-icons.js
//
// Self-contained monoline icon set for the tyre subsystem. Each icon is a 24x24
// stroke SVG that inherits `currentColor`, so the badge colour drives the icon.
// Kept inline (rather than pulling from the mixed /assets icon folder) so the set
// stays visually consistent and can never 404 in a build.

const svg = (paths) =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ` +
  `stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">${paths}</svg>`;

// A few icons in this set are solid rather than monoline, because they are lifted
// from /assets to match glyphs already in use elsewhere in the product. They take
// currentColor too, so they still follow whatever colour the badge sets.
const svgSolid = (paths) =>
  `<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" ` +
  `focusable="false">${paths}</svg>`;

export const TYRE_ICONS = {
  // Clipboard list — the tyre register
  register: svg(
    `<rect x="5" y="4" width="14" height="17" rx="2"/>` +
    `<path d="M9 4V3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V4"/>` +
    `<path d="M9 10h6M9 14h6M9 18h4"/>`
  ),

  // Two-way swap — fit / remove a tyre from a sensor
  // Bi-directional arrows — fit a tyre to a sensor (and move it between sensors).
  // Rescaled from assets/icons/notification-log-pair1.svg, which is the same glyph
  // the notification log already uses for pairing.
  fit: svg(
    `<path d="M3.5 8.5H19"/><path d="M14.5 5 19 8.5 14.5 12"/>` +
    `<path d="M19 15.5H3.5"/><path d="M8 12 3.5 15.5 8 19"/>`
  ),

  // Relocate to a dashed destination slot — Move (re-fit to a different sensor).
  // Distinct from `rotate` so Move and Rotate don't share a glyph on the detail bar.
  move: svg(
    `<rect x="3.5" y="3.5" width="8" height="8" rx="1.5"/>` +
    `<rect x="12.5" y="12.5" width="8" height="8" rx="1.5" stroke-dasharray="2.4 2.4"/>` +
    `<path d="M9.5 9.5 15 15"/><path d="M15 11.5V15h-3.5"/>`
  ),

  // Clipboard with a tick — inspection
  inspect: svg(
    `<rect x="5" y="4" width="14" height="17" rx="2"/>` +
    `<path d="M9 4V3.5A1.5 1.5 0 0 1 10.5 2h3A1.5 1.5 0 0 1 15 3.5V4"/>` +
    `<path d="M8.5 13.5l2 2 4-4.5"/>`
  ),

  // Circular arrows — rotate tyres
  rotate: svg(
    `<path d="M20 12a8 8 0 1 1-2.3-5.6"/>` +
    `<path d="M20 4v4h-4"/>`
  ),

  // Wrench — repair
  repair: svg(
    `<path d="M15.5 8.5a3.5 3.5 0 0 1-4.6 4.6L5 19l-1.5-1.5 5.9-5.9a3.5 3.5 0 0 1 4.6-4.6l-2.2 2.2 1.5 1.5 2.2-2.2z"/>`
  ),

  // Fresh tread band with grooves + a renew arrow — retread (a new tread laid on
  // the casing). Kept visually distinct from the repair wrench.
  retread: svg(
    `<rect x="3.5" y="14" width="17" height="6" rx="1.5"/>` +
    `<path d="M6 14v6M10 14v6M14 14v6M18 14v6"/>` +
    `<path d="M12 11V4"/><path d="M8.5 7.5 12 4l3.5 3.5"/>`
  ),

  // Yards - Warehouse — Depot
  yards: svg(
    `<path d="M3 10l9-5 9 5v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/>` +
    `<path d="M8 21v-6h8v6"/><path d="M8 13h8"/>`
  ),

  // Contact card — supplier management.
  supplier: svg(
    `<rect x="4" y="3.5" width="16" height="17" rx="2"/>` +
    `<circle cx="9" cy="9" r="2.2"/>` +
    `<path d="M6.5 14c.8-1.5 2-2.3 3.5-2.3s2.7.8 3.5 2.3"/>` +
    `<path d="M15.5 8h2.2M15.5 11.5h2.2M15.5 15h2.2"/>`
  ),

  // Tyre catalogue — a small catalogue/book with a tyre-like ring.
  catalogue: svg(
    `<path d="M4 4.5A2.5 2.5 0 0 1 6.5 2H11v18H6.5A2.5 2.5 0 0 0 4 22.5z"/>` +
    `<path d="M20 4.5A2.5 2.5 0 0 0 17.5 2H13v18h4.5a2.5 2.5 0 0 1 2.5 2.5z"/>` +
    `<circle cx="16.5" cy="8.5" r="2.2"/>` +
    `<path d="M15.2 8.5h2.6M16.5 7.2v2.6"/>`
  ),

  // Bar chart with trend line — reports. Transcribed from
  // assets/icons/chart-blue.svg so this matches the chart glyph used elsewhere.
  reports: svgSolid(
    `<path d="M1.25 21H4.75C4.8163 21 4.87989 20.9737 4.92678 20.9268C4.97366 20.8799 5 20.8163 5 20.75V18.75C5 18.4848 4.89464 18.2304 4.70711 18.0429C4.51957 17.8554 4.26522 17.75 4 17.75H2C1.73478 17.75 1.48043 17.8554 1.29289 18.0429C1.10536 18.2304 1 18.4848 1 18.75V20.75C1 20.8163 1.02634 20.8799 1.07322 20.9268C1.12011 20.9737 1.1837 21 1.25 21Z"/>` +
    `<path d="M8 12.75C7.73478 12.75 7.48043 12.8554 7.29289 13.0429C7.10536 13.2304 7 13.4848 7 13.75V20.75C7 20.8163 7.02634 20.8799 7.07322 20.9268C7.12011 20.9737 7.1837 21 7.25 21H10.75C10.8163 21 10.8799 20.9737 10.9268 20.9268C10.9737 20.8799 11 20.8163 11 20.75V13.75C11 13.4848 10.8946 13.2304 10.7071 13.0429C10.5196 12.8554 10.2652 12.75 10 12.75H8Z"/>` +
    `<path d="M14 14.75C13.7348 14.75 13.4804 14.8554 13.2929 15.0429C13.1054 15.2304 13 15.4848 13 15.75V20.75C13 20.8163 13.0263 20.8799 13.0732 20.9268C13.1201 20.9737 13.1837 21 13.25 21H16.75C16.8163 21 16.8799 20.9737 16.9268 20.9268C16.9737 20.8799 17 20.8163 17 20.75V15.75C17 15.4848 16.8946 15.2304 16.7071 15.0429C16.5196 14.8554 16.2652 14.75 16 14.75H14Z"/>` +
    `<path d="M20 7.75C19.7348 7.75 19.4804 7.85536 19.2929 8.04289C19.1054 8.23043 19 8.48478 19 8.75V20.75C19 20.8163 19.0263 20.8799 19.0732 20.9268C19.1201 20.9737 19.1837 21 19.25 21H22.75C22.8163 21 22.8799 20.9737 22.9268 20.9268C22.9737 20.8799 23 20.8163 23 20.75V8.75C23 8.48478 22.8946 8.23043 22.7071 8.04289C22.5196 7.85536 22.2652 7.75 22 7.75H20Z"/>` +
    `<path d="M20.9996 -0.25C20.4691 -0.25 19.9604 -0.0392863 19.5854 0.335786C19.2103 0.710859 18.9996 1.21957 18.9996 1.75C19.0008 1.90462 19.0176 2.05873 19.0496 2.21C19.0591 2.24802 19.0595 2.28775 19.0508 2.32596C19.0421 2.36418 19.0246 2.39982 18.9996 2.43L15.7396 6.23C15.7087 6.26431 15.6693 6.28981 15.6254 6.30386C15.5815 6.31792 15.5346 6.32004 15.4896 6.31C15.3293 6.26984 15.1648 6.24969 14.9996 6.25C14.7357 6.24979 14.4743 6.30182 14.2306 6.40308C13.9869 6.50434 13.7656 6.65283 13.5796 6.84C13.5486 6.87376 13.5083 6.8975 13.4637 6.90819C13.4192 6.91888 13.3725 6.91603 13.3296 6.9L11.1496 6.18C11.1118 6.16453 11.0783 6.14004 11.0522 6.10866C11.026 6.07728 11.008 6.03997 10.9996 6C10.9624 5.46957 10.7161 4.97561 10.3148 4.62679C9.91347 4.27797 9.39001 4.10287 8.85958 4.14C8.32914 4.17713 7.83519 4.42345 7.48637 4.82478C7.13755 5.22611 6.96245 5.74957 6.99958 6.28C6.98379 6.43293 6.98379 6.58707 6.99958 6.74C7.0258 6.77828 7.03984 6.8236 7.03984 6.87C7.03984 6.9164 7.0258 6.96172 6.99958 7L4.06958 9.38C4.03041 9.40385 3.98543 9.41646 3.93958 9.41646C3.89372 9.41646 3.84875 9.40385 3.80958 9.38C3.41696 9.2062 2.97883 9.16336 2.55999 9.25782C2.14114 9.35229 1.76382 9.57904 1.48381 9.90454C1.2038 10.23 1.03598 10.637 1.00516 11.0653C0.974342 11.4935 1.08216 11.9203 1.31268 12.2826C1.54319 12.6448 1.88417 12.9233 2.28517 13.0767C2.68618 13.2302 3.12592 13.2505 3.53938 13.1347C3.95283 13.0189 4.31805 12.7731 4.58102 12.4337C4.84398 12.0943 4.99074 11.6793 4.99958 11.25C5.01488 11.097 5.01488 10.943 4.99958 10.79C4.97661 10.7524 4.96446 10.7091 4.96446 10.665C4.96446 10.6209 4.97661 10.5776 4.99958 10.54L7.89958 8.12C7.93875 8.09615 7.98372 8.08354 8.02958 8.08354C8.07543 8.08354 8.12041 8.09615 8.15958 8.12C8.42703 8.22302 8.7135 8.26735 8.99958 8.25C9.26349 8.25021 9.52483 8.19818 9.76855 8.09692C10.0123 7.99566 10.2335 7.84717 10.4196 7.66C10.4505 7.62624 10.4909 7.6025 10.5354 7.59181C10.58 7.58112 10.6267 7.58397 10.6696 7.6L12.8496 8.32C12.8951 8.33504 12.9353 8.36283 12.9654 8.40006C12.9955 8.43729 13.0143 8.48239 13.0196 8.53C13.0585 8.81248 13.1574 9.08336 13.3097 9.32447C13.4619 9.56559 13.6639 9.77136 13.9022 9.928C14.1405 10.0846 14.4095 10.1885 14.6912 10.2326C14.9729 10.2768 15.2608 10.2602 15.5356 10.1839C15.8104 10.1077 16.0656 9.97357 16.2843 9.79058C16.503 9.60758 16.68 9.37994 16.8035 9.12292C16.927 8.8659 16.9941 8.58544 17.0003 8.30036C17.0066 8.01528 16.9517 7.73216 16.8396 7.47C16.8157 7.43083 16.8031 7.38586 16.8031 7.34C16.8031 7.29414 16.8157 7.24917 16.8396 7.21L19.8396 3.66C19.874 3.61872 19.9208 3.58961 19.973 3.577C20.0252 3.56439 20.0801 3.56895 20.1296 3.59C20.3801 3.69384 20.6484 3.74818 20.9196 3.75C21.45 3.75 21.9587 3.53929 22.3338 3.16421C22.7089 2.78914 22.9196 2.28043 22.9196 1.75C22.9196 1.21957 22.7089 0.710859 22.3338 0.335786C21.9587 -0.0392863 21.45 -0.25 20.9196 -0.25H20.9996Z"/>` +
    `<path d="M1 24.25H23C23.2652 24.25 23.5196 24.1446 23.7071 23.9571C23.8946 23.7696 24 23.5152 24 23.25C24.0004 23.0626 23.9481 22.8788 23.8491 22.7197C23.75 22.5606 23.6083 22.4325 23.44 22.35C23.3023 22.2852 23.1522 22.2511 23 22.25H1C0.847822 22.2511 0.697689 22.2852 0.560002 22.35C0.391699 22.4325 0.249958 22.5606 0.150943 22.7197C0.051929 22.8788 -0.000373071 23.0626 2.00323e-06 23.25C2.00323e-06 23.5152 0.105359 23.7696 0.292895 23.9571C0.480432 24.1446 0.734786 24.25 1 24.25Z"/>`
  ),

  // Magnifier — find a tyre
  find: svg(
    `<circle cx="11" cy="11" r="6"/><path d="M20 20l-4.3-4.3"/>`
  ),

  // Small tyre glyph used inside stat tiles / hub fallback
  tyre: svg(
    `<circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="3.4"/>` +
    `<path d="M12 3v3.6M12 17.4V21M3 12h3.6M17.4 12H21"/>`
  ),

  // Location pin — a yard (a physical site tyres live at). Deliberately distinct
  // from the `stock` warehouse, which is a status, not a place.


  pin: svg(
    `<path d="M12 2a7 7 0 0 0-7 7c0 4.8 7 12 7 12s7-7.2 7-12a7 7 0 0 0-7-7z"/>` +
    `<circle cx="12" cy="9" r="2.5"/>`
  ),

  // Plain checkmark — selected / active indicator
  check: svg(`<path d="M5 12.5l4 4 10-11"/>`),

  // Back chevron
  back: svg(`<path d="M15 5l-7 7 7 7"/>`),

  // Plus in a circle — add a new tyre
  new: svg(
    `<circle cx="12" cy="12" r="9"/>` +
    `<path d="M12 8.5v7M8.5 12h7"/>`
  ),

  // Pencil — edit a tyre's details
  edit: svg(
    `<path d="M4 20h4L18.5 9.5a2 2 0 0 0 0-2.8l-1.2-1.2a2 2 0 0 0-2.8 0L4 16z"/>` +
    `<path d="M13.5 6.5l4 4"/>`
  ),

  // Printer — print a document (a retread consignment sheet). Monoline to match
  // the set; the sheet feeds in on top and emerges in the output tray below.
  print: svg(
    `<path d="M6 9V2h12v7"/>` +
    `<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>` +
    `<rect x="6" y="14" width="12" height="8" rx="1"/>`
  ),

  // Envelope — email a document (a retread consignment sheet).
  mail: svg(
    `<rect x="3" y="5" width="18" height="14" rx="2"/>` +
    `<path d="M3.5 6.5l8.5 6.5 8.5-6.5"/>`
  ),

  // Tray with an arrow coming in — import tyres from a spreadsheet
  import: svg(
    `<path d="M12 4v10"/>` +
    `<path d="M8 10l4 4 4-4"/>` +
    `<path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/>`
  ),

  // Lidded box — retire a tyre out of the fleet
  retire: svg(
    `<path d="M3 7h18v3H3z"/>` +
    `<path d="M5 10v9a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-9"/>` +
    `<path d="M10 14h4"/>`
  ),

  // Box with a downward arrow into it — archive a retired tyre (reversible, so a
  // different glyph from Retire's closed lid)
  archive: svg(
    `<path d="M3 5h18v4H3z"/>` +
    `<path d="M5 9v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9"/>` +
    `<path d="M12 12v5"/>` +
    `<path d="M9.5 14.5L12 17l2.5-2.5"/>`
  ),

  // Counter-clockwise arrow — restore an archived tyre
  restore: svg(
    `<path d="M4 12a8 8 0 1 0 2.35-5.65"/>` +
    `<path d="M4 4v4h4"/>`
  )
};

export function icon(name) {
  return TYRE_ICONS[name] || '';
}
